import type { Archetype, GiverArchetypeKey, RecipientArchetypeKey, Answers } from '../types';

// Giver Archetypes (extended from existing)
export const giverArchetypes: Record<GiverArchetypeKey, Archetype> = {
  "Meaning-Maker": {
    color: "from-purple-500 to-pink-500",
    description: "You create deeply symbolic gifts that strengthen emotional bonds",
    traits: ["Thoughtful symbolism", "Deep care expression", "Relationship depth focus"]
  },
  "Joy Bringer": {
    color: "from-yellow-400 to-orange-500",
    description: "You specialize in surprise and delight, making every gift a celebration",
    traits: ["Surprise & delight focus", "Fun prioritization", "Playful approach"]
  },
  "Anxious Perfectionist": {
    color: "from-blue-400 to-indigo-500",
    description: "You care deeply about finding the perfect gift and may seek guidance",
    traits: ["High gift anxiety", "Guidance-seeking", "Perfect-fit obsession"]
  },
  "Strategic Minimalist": {
    color: "from-green-400 to-teal-500",
    description: "You curate essential, high-impact gifts with efficiency and alignment",
    traits: ["Curates essentials", "Efficient approach", "Quality over quantity"]
  },
  "Creative Crafter": {
    color: "from-pink-400 to-rose-500",
    description: "You prefer personalized, handmade gifts that show time and creativity",
    traits: ["DIY preference", "Personalized expression", "Creative identity"]
  },
  "Generous Splurger": {
    color: "from-amber-400 to-yellow-500",
    description: "You show love through lavish, expensive gifts and abundance",
    traits: ["High budget comfort", "Expensive = caring belief", "Abundance mindset"]
  },
  "Ritual Romantic": {
    color: "from-rose-400 to-pink-600",
    description: "You use gifting as love ritual, emphasizing timing and romantic symbolism",
    traits: ["Tradition-oriented", "Planned anniversaries", "Devotion expression"]
  },
  "Connector": {
    color: "from-emerald-400 to-teal-600",
    description: "You prefer gifts that bring people together and foster relationships",
    traits: ["Tribe-building focus", "Group experiences", "Relationship facilitation"]
  },
  "Surprise Architect": {
    color: "from-violet-400 to-purple-600",
    description: "You derive pleasure from elaborate reveals and mystery orchestration",
    traits: ["Elaborate reveals", "Mystery orchestration", "Creative planning"]
  },
  "Fixer/Provider": {
    color: "from-slate-400 to-gray-600",
    description: "You focus on practical utilities and problem-solving through gifts",
    traits: ["Problem-solver orientation", "Practical utilities", "Helper identity"]
  },
  "Competitive Gifter": {
    color: "from-red-400 to-orange-600",
    description: "You give impressive luxury gifts to outdo others and maintain reputation",
    traits: ["Wants to outdo others", "Impressive luxury focus", "Reputation-conscious"]
  },
  "Obligated Giver": {
    color: "from-gray-400 to-slate-500",
    description: "You give out of duty or social expectation, often defaulting to generic gifts",
    traits: ["Duty-driven approach", "Social norm following", "Generic tendencies"]
  }
};

// Recipient Archetypes (new from research)
export const recipientArchetypes: Record<RecipientArchetypeKey, Archetype> = {
  "Experiential Creative": {
    color: "from-purple-500 to-indigo-600",
    description: "Thrives on workshops, immersive experiences, and creative skill-building",
    traits: ["High openness", "Novelty-seeking", "Creative pursuits", "Quality-time love language"]
  },
  "Sentimental Storykeeper": {
    color: "from-pink-500 to-rose-600",
    description: "Treasures keepsakes, memory books, and tradition-based gifts",
    traits: ["High sentimentality", "Words of affirmation", "Memory focus", "Emotional attachment"]
  },
  "Aesthetic Curator": {
    color: "from-indigo-500 to-purple-600",
    description: "Appreciates beautiful objects, boutique goods, and artisanal products",
    traits: ["Strong aesthetic sense", "Craftsmanship values", "Visual sensitivity", "Design appreciation"]
  },
  "Novelty Seeker": {
    color: "from-orange-500 to-red-600",
    description: "Loves travel, adventure activities, and spontaneous experiences",
    traits: ["High extraversion", "Loves surprises", "Social experiences", "Adrenaline-seeking"]
  },
  "Cultural Explorer": {
    color: "from-teal-500 to-green-600",
    description: "Enjoys art experiences, cultural events, and cross-cultural gifts",
    traits: ["Intellectual curiosity", "Solo/paired adventures", "Perspective-seeking", "Learning orientation"]
  },
  "Playful Escapist": {
    color: "from-yellow-500 to-orange-600",
    description: "Appreciates inside jokes, retro gifts, and entertainment experiences",
    traits: ["Humor appreciation", "Nostalgia", "Entertainment-driven", "Childlike wonder"]
  },
  "Useful Pragmatist": {
    color: "from-gray-500 to-slate-600",
    description: "Values tools, upgrades, and problem-solving products above sentimentality",
    traits: ["Utility focus", "Acts-of-service love language", "Efficiency-oriented", "Low sentimentality"]
  },
  "Grounded Homemaker": {
    color: "from-green-500 to-emerald-600",
    description: "Loves cookbooks, home essentials, and family-oriented experiences",
    traits: ["Family-centric", "Home comfort", "Nurturing orientation", "Traditional values"]
  },
  "Intentional Minimalist": {
    color: "from-slate-500 to-gray-600",
    description: "Prefers zero-waste items, experiences over objects, and donation gifts",
    traits: ["Minimalist aesthetic", "Ethics & quality focus", "Anti-clutter", "Environmental consciousness"]
  },
  "Romantic Idealist": {
    color: "from-rose-500 to-pink-600",
    description: "Treasures personalized jewelry, symbolic gestures, and romantic experiences",
    traits: ["Symbolic romance", "High sentimentality", "Relationship-focused", "Idealization tendencies"]
  },
  "Empathic Giver": {
    color: "from-blue-500 to-indigo-600",
    description: "Enjoys care packages, wellness products, and acts of service",
    traits: ["High agreeableness", "Wellness/care focus", "Reciprocal nature", "Helper orientation"]
  },
  "Social Connector": {
    color: "from-emerald-500 to-teal-600",
    description: "Values group activities, games, and community-building gifts",
    traits: ["Community builder", "Group experiences", "Relationship-focused", "Socially energized"]
  },
  "Status Seeker": {
    color: "from-amber-500 to-yellow-600",
    description: "Appreciates designer items, exclusive experiences, and prestige products",
    traits: ["Brand consciousness", "Luxury preference", "Image-focused", "Competitive orientation"]
  },
  "Tech Enthusiast": {
    color: "from-cyan-500 to-blue-600",
    description: "Loves latest devices, smart home tech, and cutting-edge tools",
    traits: ["Early adopter tendencies", "Gadget interest", "Innovation appreciation", "Efficiency through technology"]
  },
  "Wellness Devotee": {
    color: "from-green-500 to-teal-600",
    description: "Values spa treatments, fitness gear, and wellness experiences",
    traits: ["Health focus", "Self-care prioritization", "Optimization mindset", "Mindfulness orientation"]
  },
  "Collector Specialist": {
    color: "from-purple-500 to-violet-600",
    description: "Seeks rare finds, hobby supplies, and expert-level items",
    traits: ["Deep niche passion", "Expertise-seeking", "Detail-oriented", "Collection-building behavior"]
  },
  "Climate-Principled": {
    color: "from-green-600 to-emerald-700",
    description: "Values sustainable products, eco-friendly experiences, and environmental donations",
    traits: ["Strong sustainability ethos", "Ethical consumption", "Environmental activism", "Future-focused"]
  },
  "Digital Nostalgist": {
    color: "from-indigo-600 to-purple-700",
    description: "Appreciates physical photo books, printed digital art, and analog experiences",
    traits: ["Values tangible nostalgia", "Memory preservation", "Analog appreciation", "Digital-to-physical conversion"]
  }
};

// Legacy export for backward compatibility
export const archetypes = giverArchetypes;

// Archetype calculation logic
export const calculateArchetype = (answers: Answers): GiverArchetypeKey => {
  // Question 2: confidence level
  const confidence = answers[2];
  
  // Question 2 followup: motivation  
  const motivation = answers['2_followup'];
  
  // Question 3: style preference
  const style = answers[3];
  
  // Question 4: social preference
  const social = answers[4];
  
  // Question 5: values (array)
  const values = answers[5] || [];
  
  // Question 6: independence level
  const independence = answers[6];
  
  // Question 6 followup: planning style
  const planning = answers['6_followup'];

  // Scoring system
  const scores = {
    "Meaning-Maker": 0,
    "Joy Bringer": 0,
    "Anxious Perfectionist": 0,
    "Strategic Minimalist": 0,
    "Creative Crafter": 0,
    "Generous Splurger": 0
  };

  // Confidence scoring
  if (confidence === "😰 Stressed") {
    scores["Anxious Perfectionist"] += 3;
  } else if (confidence === "😊 Excited") {
    scores["Joy Bringer"] += 2;
    scores["Creative Crafter"] += 1;
  }

  // Motivation scoring
  if (typeof motivation === 'object' && motivation && 'text' in motivation) {
    switch (motivation.text) {
      case "Show care":
        scores["Meaning-Maker"] += 3;
        break;
      case "Make them happy":
        scores["Joy Bringer"] += 3;
        break;
      case "Nail the perfect thing":
        scores["Anxious Perfectionist"] += 3;
        break;
      case "Impress them":
        scores["Generous Splurger"] += 2;
        scores["Anxious Perfectionist"] += 1;
        break;
      case "Solve a problem":
        scores["Strategic Minimalist"] += 3;
        break;
    }
  }

  // Style scoring
  if (style === "Very Unique" || style === "Somewhat Unique") {
    scores["Creative Crafter"] += 2;
    scores["Joy Bringer"] += 1;
  } else if (style === "Very Classic" || style === "Somewhat Classic") {
    scores["Strategic Minimalist"] += 2;
    scores["Meaning-Maker"] += 1;
  }

  // Social scoring
  if (social === "Shared Experiences" || social === "Mostly Shared") {
    scores["Joy Bringer"] += 2;
  } else if (social === "Private Moments" || social === "Mostly Private") {
    scores["Meaning-Maker"] += 2;
    scores["Creative Crafter"] += 1;
  }

  // Values scoring
  if (Array.isArray(values)) {
    values.forEach((value) => {
      if (typeof value === 'object' && value && 'text' in value) {
        switch (value.text) {
          case "Thought over price":
            scores["Meaning-Maker"] += 2;
            scores["Creative Crafter"] += 1;
            break;
          case "I love a bargain":
            scores["Strategic Minimalist"] += 2;
            break;
          case "I splurge to show I care":
            scores["Generous Splurger"] += 3;
            break;
          case "Gifts should impress":
            scores["Generous Splurger"] += 2;
            scores["Anxious Perfectionist"] += 1;
            break;
          case "High quality over quantity":
            scores["Strategic Minimalist"] += 2;
            scores["Meaning-Maker"] += 1;
            break;
        }
      }
    });
  }

  // Independence scoring
  if (independence === "Strongly Disagree" || independence === "Disagree") {
    scores["Creative Crafter"] += 2;
    scores["Strategic Minimalist"] += 1;
  } else if (independence === "Agree" || independence === "Strongly Agree") {
    scores["Anxious Perfectionist"] += 2;
  }

  // Planning scoring
  if (planning === "Planner") {
    scores["Strategic Minimalist"] += 1;
    scores["Meaning-Maker"] += 1;
  } else if (planning === "Spontaneous") {
    scores["Joy Bringer"] += 1;
  }

  // Find highest scoring archetype
  const maxScore = Math.max(...Object.values(scores));
  const topArchetypes = Object.entries(scores).filter(([, score]) => score === maxScore);
  
  // If tie, use tiebreaker logic
  if (topArchetypes.length > 1) {
    // Prefer based on strongest indicators
    if (scores["Generous Splurger"] === maxScore) return "Generous Splurger";
    if (scores["Anxious Perfectionist"] === maxScore) return "Anxious Perfectionist";
    if (scores["Creative Crafter"] === maxScore) return "Creative Crafter";
    if (scores["Joy Bringer"] === maxScore) return "Joy Bringer";
    if (scores["Meaning-Maker"] === maxScore) return "Meaning-Maker";
    return "Strategic Minimalist";
  }

  return topArchetypes[0][0] as GiverArchetypeKey;
}; 